import { Divider, Form, Input, Radio, Select, SelectProps } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { useLayoutEffect, useMemo, useState, useEffect } from 'react';
import { useAuth } from '@frontegg/react';
import {
  PractitionerSearchPreferenceScreen,
  useGetPractitionerSearchPreference,
  useSetPractitionerSearchPreference,
} from 'src/hooks/practitioner';
import { EncounterStatus, AppointmentChatCompletionStatus } from 'src/models';
import {
  Plus,
  Settings04,
  ClockRewind,
  Trash02,
} from '@insight-health-ai/roseate/icons';
import {
  Button,
  Drawer,
  Status,
  Table,
  Card,
  toast,
} from '@insight-health-ai/roseate/components';
import { EditableField } from 'src/components';

export type AppointmentSavedView = {
  version?: 2;
  filters?: {
    practitionerIds?: number[];
    inVisitStatus?: EncounterStatus[];
    chatCompletionStatus?: AppointmentChatCompletionStatus[];
    patientIds?: number[];
    date?: {
      startDate?: string | null;
      endDate?: string | null;
    };
  };
  title: string;
  id: string;
  last_modified_by: string | null;
  created_by: string;
  isDefaultAppointmentView?: boolean;
};

type NewAppointmentSavedView = Pick<AppointmentSavedView, 'filters'>;

type AppointmentSavedViewDropdownProps = {
  screen: PractitionerSearchPreferenceScreen;
  defaultViewId?: AppointmentSavedView['id'] | null;
  selectedViewId?: AppointmentSavedView['id'] | null;
  onChange: (
    id: AppointmentSavedView['id'] | undefined,
    value: AppointmentSavedView | null
  ) => void;
  selectProps?: SelectProps;
  systemDefaultView?: AppointmentSavedView;
  onDefaultChange?: (
    id: AppointmentSavedView['id'] | undefined,
    value: AppointmentSavedView | null
  ) => void;
  getCurrentView: () => NewAppointmentSavedView;
};

export const AppointmentSavedViewDropdown = (
  props: AppointmentSavedViewDropdownProps
) => {
  const {
    screen,
    selectedViewId,
    onChange,
    selectProps,
    systemDefaultView,
    onDefaultChange,
    defaultViewId,
    getCurrentView,
  } = props;
  const { user } = useAuth();
  const { data: recentSearch } =
    useGetPractitionerSearchPreference<AppointmentSavedView>({ screen });

  const [manageViewModal, setManageViewModal] = useState<{
    new?: NewAppointmentSavedView;
  } | null>(null);

  const { mutateAsync: setPractitionerSearchPreference } =
    useSetPractitionerSearchPreference<AppointmentSavedView>();

  const viewOptions = useMemo(() => {
    const list = [...(recentSearch?.search_preference || [])];
    if (systemDefaultView) {
      list.unshift(systemDefaultView);
    }
    return list;
  }, [recentSearch, systemDefaultView]);

  const options = useMemo(() => {
    return [
      ...viewOptions.map((preference) => ({
        label: preference.title,
        value: preference.id,
        preference,
      })),
    ];
  }, [viewOptions]);

  const handleSelect = async (
    value: AppointmentSavedView['id'],
    option: (typeof options)[number]
  ) => {
    onChange(value, option.preference);
  };

  const [newViewForm] = Form.useForm();

  const handleDefaultChange = async (
    id: AppointmentSavedView['id'] | undefined,
    _: AppointmentSavedView | null
  ) => {
    const payload = (recentSearch?.search_preference || []).map(
      (preference) => {
        return {
          ...preference,
          isDefaultAppointmentView: preference.id === id,
        };
      }
    );
    try {
      await setPractitionerSearchPreference({ screen, preferences: payload });
    } catch (e) {
      toast.error('Failed to set default view');
    }
  };

  const handleCreateView = async ({
    newView,
    isDefaultAppointmentView,
    title,
  }: {
    newView: NewAppointmentSavedView;
    isDefaultAppointmentView: boolean;
    title: string;
  }) => {
    const preferences = [
      ...(recentSearch?.search_preference || []).map((preference) =>
        isDefaultAppointmentView
          ? { ...preference, isDefaultAppointmentView: false }
          : preference
      ),
      {
        version: 2 as const,
        id: uuidv4(),
        last_modified_by: user?.email || '',
        created_by: user?.email || '',
        ...newView,
        isDefaultAppointmentView: !!isDefaultAppointmentView,
        title,
      },
    ];
    try {
      await setPractitionerSearchPreference({ screen, preferences });
      setManageViewModal({});
      toast.success('View created successfully');
    } catch (e) {
      toast.error('Failed to create view');
    }
  };

  const handleUpdateView = async (
    id: AppointmentSavedView['id'],
    value: Partial<AppointmentSavedView>
  ) => {
    const payload = (recentSearch?.search_preference || []).map(
      (preference) => {
        return preference.id === id ? { ...preference, ...value } : preference;
      }
    );
    try {
      await setPractitionerSearchPreference({ screen, preferences: payload });
    } catch (e) {
      toast.error('Failed to update view');
    }
  };

  const handleDeleteView = async (id: AppointmentSavedView['id']) => {
    const payload = (recentSearch?.search_preference || []).filter(
      (preference) => preference.id !== id
    );
    try {
      await setPractitionerSearchPreference({ screen, preferences: payload });
    } catch (e) {
      toast.error('Failed to delete view');
    }
  };

  useEffect(() => {
    if (!manageViewModal?.new) {
      newViewForm.resetFields();
    }
  }, [manageViewModal?.new, newViewForm]);

  useLayoutEffect(() => {
    const defaultViewOption = options.find(
      (option) => option.preference?.isDefaultAppointmentView
    );
    if (defaultViewOption) {
      if (defaultViewOption?.preference?.id !== defaultViewId) {
        onDefaultChange?.(
          defaultViewOption?.preference?.id,
          defaultViewOption?.preference as AppointmentSavedView
        );
      }
    } else if (systemDefaultView) {
      onDefaultChange?.(systemDefaultView?.id, systemDefaultView);
    }
  }, [options]);

  const renderViewFilters = (val: AppointmentSavedView['filters']) => {
    const filters = val || {};
    return (
      <div className="flex flex-col gap-1">
        <div className="flex gap-2">
          <span className="font-medium">Practitioners:</span>
          {filters.practitionerIds?.length ? (
            <span>{filters.practitionerIds.length} Practitioners</span>
          ) : (
            <span>All</span>
          )}
        </div>
        {!!filters.patientIds?.length && (
          <div className="flex gap-2">
            <span className="font-medium">Patients:</span>
            <span>{filters.patientIds.length} Patients</span>
          </div>
        )}
        <div className="flex gap-2">
          <span className="font-medium">Date:</span>
          {filters.date?.startDate || filters.date?.endDate ? (
            <span>
              {[
                filters.date.startDate,
                filters.date.endDate != filters.date.startDate &&
                  filters.date.endDate,
              ]
                .filter(Boolean)
                .join(' - ')}
            </span>
          ) : (
            <span>All time</span>
          )}
        </div>
        {!!filters.chatCompletionStatus?.length && (
          <div className="flex gap-2">
            <span className="font-medium">Intake status:</span>
            <span>{filters.chatCompletionStatus.join(', ')}</span>
          </div>
        )}
        {!!filters.inVisitStatus?.length && (
          <div className="flex gap-2">
            <span className="font-medium">In-visit status:</span>
            <span>{filters.inVisitStatus.join(', ')}</span>
          </div>
        )}
      </div>
    );
  };

  const manageDrawerColumns = useMemo(() => {
    return [
      {
        title: 'Name',
        dataIndex: 'title',
        key: 'title',
        render: (
          val: AppointmentSavedView['title'],
          record: AppointmentSavedView
        ) => {
          return record.id === systemDefaultView?.id ? (
            val
          ) : (
            <EditableField
              text={val}
              onSave={(value) => {
                handleUpdateView(record.id, { title: value });
              }}
            />
          );
        },
      },
      {
        title: 'Filters',
        dataIndex: 'filters',
        key: 'filters',
        render: (val: AppointmentSavedView['filters']) => {
          return renderViewFilters(val);
        },
      },
      {
        title: 'Default',
        dataIndex: 'isDefaultAppointmentView',
        key: 'isDefaultAppointmentView',
        render: (
          val: AppointmentSavedView['isDefaultAppointmentView'],
          record: AppointmentSavedView
        ) => {
          return (
            <div className="flex justify-center">
              <Radio
                checked={
                  record.id === systemDefaultView?.id
                    ? record.id === defaultViewId
                    : !!val
                }
                onChange={() => {
                  handleDefaultChange(record.id, record);
                }}
              />
            </div>
          );
        },
      },
      {
        title: '',
        key: 'actions',
        render: (_: unknown, record: AppointmentSavedView) => {
          if (record.id === systemDefaultView?.id) {
            return <span className="text-gray-400">Built-in view</span>;
          }
          return (
            <Button
              variant="text"
              appearance="danger"
              icon={<Trash02 />}
              onClick={() => {
                handleDeleteView(record.id);
              }}
            >
              Delete
            </Button>
          );
        },
      },
    ];
  }, [defaultViewId, systemDefaultView]);

  return (
    <>
      <Select
        prefix={<ClockRewind />}
        placeholder="Custom views"
        {...selectProps}
        popupRender={(menu) => {
          return (
            <>
              <div className="px-2 py-1 font-medium text-gray-500">
                My custom views
              </div>
              <Divider style={{ margin: '0 0 8px' }} />
              {menu}
              <Divider style={{ margin: '8px 0 0' }} />
              <div className="flex justify-between items-center gap-2 p-1">
                <Button
                  variant="text"
                  icon={<Plus />}
                  onClick={() => {
                    const newView = getCurrentView();
                    setManageViewModal({
                      new: newView,
                    });
                  }}
                >
                  Save current view
                </Button>
                <Divider className="h-[32px]" type="vertical" />
                <Button
                  variant="text"
                  appearance="secondary"
                  icon={<Settings04 />}
                  onClick={() => {
                    setManageViewModal({});
                  }}
                >
                  Manage views
                </Button>
              </div>
            </>
          );
        }}
        optionRender={(option) => {
          const isDefault = option.data?.preference?.id === defaultViewId;
          return (
            <div className="flex justify-between items-center gap-6">
              <div className="flex items-center gap-2">
                {option.label}
                {option.value === systemDefaultView?.id && (
                  <span className="text-xs text-gray-400">Built-in</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isDefault ? (
                  <Status size="xs" appearance="warning" label="Default View" />
                ) : (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleDefaultChange(
                        option.data?.preference?.id,
                        option.data?.preference as AppointmentSavedView
                      );
                    }}
                    className="text-xs text-primary hover:cursor-pointer hover:underline"
                  >
                    Set as default
                  </div>
                )}
              </div>
            </div>
          );
        }}
        options={options}
        value={selectedViewId}
        onSelect={handleSelect}
      />
      {!!manageViewModal && (
        <Drawer
          open={true}
          className="insight-background"
          title="Manage custom views"
          onClose={() => setManageViewModal(null)}
        >
          {manageViewModal.new && (
            <>
              <Card title="Save current view" padding="sm">
                <Form form={newViewForm} layout="vertical">
                  <Form.Item
                    label={<span className="label">Filters</span>}
                    name="filters"
                    className="field-wrapper"
                  >
                    {renderViewFilters(manageViewModal.new.filters || {})}
                  </Form.Item>
                  <Form.Item
                    label={<span className="label">Name</span>}
                    name="title"
                    className="field-wrapper"
                    rules={[{ required: true, message: 'title is required' }]}
                  >
                    <Input
                      // @ts-expect-error Ant Design Input `autoFocus` expects boolean; keep string for DOM parity with existing behavior
                      autoFocus="true"
                      className="field"
                      placeholder="Enter view title"
                    />
                  </Form.Item>
                  <Form.Item
                    layout="horizontal"
                    label={<span className="label">Set as default</span>}
                    name="isDefaultAppointmentView"
                    className="field-wrapper"
                    initialValue={true}
                    rules={[
                      { required: true, message: 'Set as default is required' },
                    ]}
                  >
                    <Radio.Group
                      options={[
                        { label: 'Yes', value: true },
                        { label: 'No', value: false },
                      ]}
                    />
                  </Form.Item>
                </Form>
                <div className="flex justify-end mt-4 gap-2">
                  <Button
                    variant="outlined"
                    appearance="secondary"
                    onClick={() => {
                      setManageViewModal(null);
                    }}
                  >
                    Go back
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={async () => {
                      const { title, isDefaultAppointmentView } =
                        await newViewForm.validateFields();
                      await handleCreateView({
                        newView: manageViewModal.new!,
                        isDefaultAppointmentView,
                        title,
                      });
                    }}
                  >
                    Save
                  </Button>
                </div>
              </Card>
              <Divider />
              <div className="text-lg mb-2 font-medium text-gray-500">
                Saved views
              </div>
            </>
          )}
          <Table
            columns={manageDrawerColumns}
            data={viewOptions}
            pagination={false}
          />
        </Drawer>
      )}
    </>
  );
};
